/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * ObsidianGraph — purpose-built React/SVG renderer for the OBSIDIAN security
 * graph. A faithful port of the signed-off vanilla renderer (obgraph.js) into
 * the real Cade app. Cytoscape is kept ONLY as a headless position solver
 * (see @cade/graph-logic runLayout); everything visible is drawn here in SVG +
 * absolutely-positioned HTML node glyphs, because the OBSIDIAN node language
 * (collar textPath, octagons, map-pins, hexagons, bowed edges) is undrawable in
 * Cytoscape.
 *
 * Drop-in for CytoscapeRenderer: same forwardRef handle + Props. See
 * graph-spec.md and obgraph.js for the source-of-truth geometry/behavior.
 */
import {
  useEffect, useRef, useImperativeHandle, forwardRef, useState, useMemo, useCallback, useId,
} from 'react';
import type { ElementDefinition } from 'cytoscape';
// DOM-free logic from the barrel; the React/SVG renderer building blocks from the
// dedicated subpath (the barrel stays server-safe — see @cade/graph-logic/index.ts).
import { getCategoryForType, resolveCriticalPath } from '@cade/graph-logic';
import {
  runLayout, ontologyGlyph, iconNameFor, categoryColors,
  edgeKind, resolveEdgeColor, bowPath, edgeWidth, edgeOpacity, buildArrowMarkers,
  arrowMarkerId, FLOW_DASH, FLOW_KEYFRAMES, ANNOTATION_DASH,
  EntityCollarGlyph, EntitySimpleGlyph, ActivityOctagonGlyph,
  BoundaryGlobeGlyph, GeoPinGlyph, MitreHexGlyph, NodeLabel,
  type GlyphClass, type LayoutPosition,
} from '@cade/graph-logic/renderer';

export type LayoutType = 'attack-path' | 'tree' | 'force' | 'layered';

export interface CytoscapeRendererHandle {
  centerOnNode: (nodeId: string) => void;
  highlightNode: (nodeId: string | null) => void;
  focusOnNode: (nodeId: string) => void;
  clearFocus: () => void;
  resize: () => void;
}

interface Props {
  elements: ElementDefinition[];
  layout: LayoutType;
  onNodeClick?: (nodeId: string, data: Record<string, unknown>) => void;
  highlightedNodeId?: string | null;
  onFocusChange?: (isFocused: boolean) => void;
  criticalPath?: string[];
  showEdgeLabels?: boolean;
  onNodeHover?: (nodeLabel: string | null, aliases?: string[]) => void;
  orientation?: 'vertical' | 'horizontal';
  /** Extension/compact density: smaller glyphs, collar suppressed. */
  density?: 'web' | 'compact';
  /** Force the no-collar (simple) entity glyph even at full zoom. */
  collar?: boolean;
}

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;
/** Auto-fit may zoom out below the user wheel floor so a wide graph always fits
 *  on screen without clipping nodes (the larger nodeSep can make a wide same-rank
 *  fan exceed the panel at MIN_ZOOM). MIN_ZOOM still floors manual wheel zoom-out. */
const FIT_MIN_ZOOM = 0.05;
/**
 * Auto-fit never MAGNIFIES the graph past the glyphs' native (1×) design size.
 * The node is rendered at `scale(view.k)`, so capping the auto-fit zoom at 1 keeps
 * a sparse graph (e.g. a 2–3 node chain) at its native, small node size instead of
 * ballooning to fill the panel. fit() still zooms OUT (down to FIT_MIN_ZOOM) to fit
 * large graphs; manual wheel zoom is unaffected (up to MAX_ZOOM), so the user can
 * zoom in for a closer look. Applies in both orientations (fit is orientation
 * agnostic; the layout solve handles TB vs LR). 1× ⇒ collar circle ~40px, label ~9.5px.
 */
const MAX_AUTO_FIT_ZOOM = 1;
/** Below this zoom we drop the collar (LOD) so labels don't turn to noise. */
const COLLAR_LOD_ZOOM = 0.75;
/** Dense-graph threshold: outside the focused neighborhood, drop the collar. */
const DENSE_NODE_COUNT = 22;

interface NodeRec {
  id: string;
  data: Record<string, unknown>;
  type: string;
  label: string;
  sub?: string;
  severity?: string;
  /**
   * DNS attack-surface baseline (PR #346/#347) — risk-overlay color is
   * derived from `validation_state` for `domain` nodes only:
   *   dangling-takeover-candidate  → red (takeover risk)
   *   unresolved-but-corroborated  → amber (low-confidence)
   *   resolves-no-service          → soft amber
   *   live-serving                 → green (healthy)
   *
   * Carried alongside severity so the rendering layer can pick either
   * without re-reading data[]. Spec:
   * docs/spec/topics/connectors/dns-domain-entity-extraction.md §1.2.
   */
  validationState?: string;
  glyphClass: GlyphClass;
  collarType: string;
  icon: string;
  isProperty: boolean;
  hub: boolean;
  category: string;
  // Ontology v2 read-flip (Phase 4): explicit v2-layer fill, when the data
  // pipeline supplies it (v2Elements.ts). Absent on the v1 graphState path,
  // so v1 rendering is unchanged.
  layerColor?: string;
}
interface EdgeRec {
  id: string;
  source: string;
  target: string;
  label: string;
  critical: boolean;
  kind: ReturnType<typeof edgeKind>;
  isProperty: boolean;
  // Ontology v2 read-flip (Phase 4): explicit v2-category line colour + dash,
  // when supplied by the data pipeline. Absent on the v1 path.
  v2Color?: string;
  v2Dashed?: boolean;
}

// v2 judgment-category edges render dashed (mirrors scripts/plot-v2-only.mjs [6,4]).
const V2_JUDGMENT_DASH = '6 4';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626', high: '#ea580c', medium: '#ca8a04', low: '#2563eb', ok: '#16a34a',
};

/** Risk overlay for DNS `domain` nodes — driven by `validation_state` per
 *  spec §1.2. Mapped to the same border-ring channel as severity. */
const VALIDATION_STATE_COLORS: Record<string, string> = {
  'dangling-takeover-candidate':  '#dc2626',
  'unresolved-but-corroborated':  '#ca8a04',
  'resolves-no-service':          '#d97706',
  'live-serving':                 '#16a34a',
};

function isNodeEl(el: ElementDefinition): boolean {
  return el.group === 'nodes' || (!el.group && !el.data.source);
}

export const ObsidianGraph = forwardRef<CytoscapeRendererHandle, Props>(function ObsidianGraph(
  { elements, layout, onNodeClick, highlightedNodeId, onFocusChange, criticalPath, showEdgeLabels, onNodeHover, orientation = 'vertical', density = 'web', collar = true },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onNodeHoverRef = useRef(onNodeHover);
  onNodeHoverRef.current = onNodeHover;
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const onFocusChangeRef = useRef(onFocusChange);
  onFocusChangeRef.current = onFocusChange;

  const scale = density === 'compact' ? 0.85 : 1;
  const collarEnabled = collar && density !== 'compact';

  // ── theme reactivity via data-theme MutationObserver (matches CytoscapeRenderer) ──
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark',
  );
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDark(el.getAttribute('data-theme') === 'dark');
    const observer = new MutationObserver(update);
    observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // ── ensure the flow keyframes are present once ──
  useEffect(() => {
    if (document.getElementById('obg-anim')) return;
    const s = document.createElement('style');
    s.id = 'obg-anim';
    s.textContent = FLOW_KEYFRAMES;
    document.head.appendChild(s);
  }, []);

  // Stable signature of the element set: re-solve layout / re-parse records only
  // when the actual node/edge ids (or layout/orientation/criticalPath) change.
  // GraphPanel and the streaming loop hand us a fresh array reference on every
  // render; without this the headless layout would re-run and reset the user's
  // pan/zoom continuously. (Equivalent to CytoscapeRenderer's prev-element-id guard.)
  const elementsSig = useMemo(
    () => elements.map((e) => (e.data.id as string) ?? `${e.data.source as string}>${e.data.target as string}`).join('|'),
    [elements],
  );

  // critical-path sets — resolved ONCE through the shared resolver and reused for
  // both the edge `critical` flags (below) and node dimming (was computed twice).
  const cpResult = useMemo(
    // deps keyed on elementsSig (stable proxy for `elements`) + criticalPath
    () => resolveCriticalPath(elements as any, criticalPath),
    [elementsSig, criticalPath],
  );
  const hasCriticalPath = cpResult.matched > 0;

  // ── parse elements into node/edge records + adjacency ──
  const { nodeRecs, edgeRecs, neighborsOf, nodeById } = useMemo(() => {
    const nodes = elements.filter(isNodeEl);
    const edges = elements.filter((e) => !isNodeEl(e));
    const degree = new Map<string, number>();
    for (const e of edges) {
      degree.set(e.data.source as string, (degree.get(e.data.source as string) ?? 0) + 1);
      degree.set(e.data.target as string, (degree.get(e.data.target as string) ?? 0) + 1);
    }
    let maxDeg = 0; let hubId = '';
    for (const [id, d] of degree) { if (d > maxDeg) { maxDeg = d; hubId = id; } }

    const nById = new Map<string, NodeRec>();
    const nRecs: NodeRec[] = nodes.map((n) => {
      const type = (n.data.type as string) ?? 'unknown';
      const og = ontologyGlyph(type);
      const rec: NodeRec = {
        id: n.data.id as string,
        data: n.data as Record<string, unknown>,
        type,
        label: (n.data.label as string) ?? (n.data.id as string),
        sub: n.data.sub as string | undefined,
        severity: n.data.severity as string | undefined,
        validationState: n.data.validation_state as string | undefined,
        glyphClass: og.glyphClass,
        collarType: og.collarType,
        icon: iconNameFor(type),
        isProperty: og.isProperty,
        hub: (n.data.id as string) === hubId && maxDeg >= 3,
        category: getCategoryForType(type),
        layerColor: n.data.layerColor as string | undefined,
      };
      nById.set(rec.id, rec);
      return rec;
    });

    const eRecs: EdgeRec[] = edges.map((e) => {
      const source = e.data.source as string;
      const target = e.data.target as string;
      const srcType = nById.get(source)?.type;
      const tgtType = nById.get(target)?.type;
      const kind = edgeKind(srcType, tgtType);
      const id = (e.data.id as string) ?? `${source}-${target}`;
      return {
        id,
        source,
        target,
        label: (e.data.label as string) ?? '',
        critical: cpResult.edgeIds.has(id),
        kind,
        isProperty: (e.data.isProperty as boolean) ?? kind === 'annotation',
        v2Color: e.data.lineColor as string | undefined,
        v2Dashed: e.data.dashed as boolean | undefined,
      };
    });

    const nb: Record<string, Record<string, true>> = {};
    for (const n of nRecs) { nb[n.id] = { [n.id]: true }; }
    for (const e of eRecs) {
      if (nb[e.source]) nb[e.source]![e.target] = true;
      if (nb[e.target]) nb[e.target]![e.source] = true;
    }
    return { nodeRecs: nRecs, edgeRecs: eRecs, neighborsOf: nb, nodeById: nById };
    // deps keyed on elementsSig (stable proxy for `elements`) + cpResult
  }, [elementsSig, cpResult]);

  // ── headless layout solve (positions in solver space) ──
  // deps keyed on elementsSig (stable proxy for `elements`)
  const positions = useMemo(() => runLayout(elements as any, layout, orientation), [elementsSig, layout, orientation]);

  // ── viewport (pan + zoom) ──
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const [size, setSize] = useState({ w: 0, h: 0 });

  // selection / hover / external highlight state (obgraph.js sel/hover/ext)
  const [sel, setSel] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const ext = highlightedNodeId ?? null;
  // Precedence: a direct graph hover wins, then an external highlight (an entity
  // chip hovered in chat — the cross-highlight bridge), then the sticky selection.
  // `ext` must outrank `sel` so chip→node cross-highlight still works while a node
  // is selected (previously `sel ?? hover ?? ext` suppressed it entirely).
  const focusId = hover ?? ext ?? sel;

  // ── fit positions into the viewport, capping zoom by node count (port of fitGraph) ──
  const fit = useCallback(() => {
    const c = containerRef.current;
    if (!c || positions.size === 0) return;
    const w = c.clientWidth || 460;
    const h = c.clientHeight || 520;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of positions.values()) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    if (!Number.isFinite(minX)) return;
    const pad = 60;
    const gw = Math.max(1, maxX - minX);
    const gh = Math.max(1, maxY - minY);
    let k = Math.min((w - 2 * pad) / gw, (h - 2 * pad) / gh);
    // Fit to the panel — min(width-fit, height-fit) above already targets the panel
    // minus padding — but never MAGNIFY past MAX_AUTO_FIT_ZOOM (native 1× node size);
    // without the cap a sparse graph (1-3 nodes) balloons to fill the panel. Floor at
    // FIT_MIN_ZOOM so a wide graph still fits without clipping. Manual wheel zoom is
    // unaffected (up to MAX_ZOOM).
    k = Math.max(FIT_MIN_ZOOM, Math.min(k, MAX_AUTO_FIT_ZOOM));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    setSize({ w, h });
    setView({ x: w / 2 - cx * k, y: h / 2 - cy * k, k });
  }, [positions]);

  // refit when positions / container change
  useEffect(() => { fit(); }, [fit]);
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const ro = new ResizeObserver(() => {
      setSize({ w: c.clientWidth, h: c.clientHeight });
      fit();
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, [fit]);

  // ── imperative handle (same surface as CytoscapeRenderer) ──
  useImperativeHandle(ref, () => ({
    centerOnNode(nodeId: string) {
      const p = positions.get(nodeId);
      const c = containerRef.current;
      if (!p || !c) return;
      const k = viewRef.current.k;
      setView({ x: c.clientWidth / 2 - p.x * k, y: c.clientHeight / 2 - p.y * k, k });
    },
    highlightNode(nodeId: string | null) {
      // external highlight is driven by the highlightedNodeId prop; this method
      // forces selection so imperative callers (centerOnNode → click) still work.
      setSel(nodeId);
    },
    focusOnNode(nodeId: string) {
      setSel(nodeId);
      onFocusChangeRef.current?.(true);
      const p = positions.get(nodeId);
      const c = containerRef.current;
      if (p && c) {
        const k = viewRef.current.k;
        setView({ x: c.clientWidth / 2 - p.x * k, y: c.clientHeight / 2 - p.y * k, k });
      }
    },
    clearFocus() {
      setSel(null);
      onFocusChangeRef.current?.(false);
    },
    resize() {
      fit();
    },
  }), [positions, fit]);

  // ── pan via pointer drag ──
  const dragRef = useRef<{ x: number; y: number; vx: number; vy: number; moved: boolean } | null>(null);
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y, moved: false };
  }, []);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) d.moved = true;
    setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }));
  }, []);
  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    dragRef.current = null;
    // empty-canvas click clears selection (only if it wasn't a drag)
    if (d && !d.moved) {
      setSel(null);
      onFocusChangeRef.current?.(false);
    }
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
  }, []);

  // ── zoom via wheel (anchored at cursor) ──
  // Attached as a NON-passive native listener: React 18 registers onWheel
  // passively at the root, so preventDefault() there is a no-op and the page
  // scrolls instead of the graph zooming.
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = c.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setView((v) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const nk = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, v.k * factor));
        // keep the point under the cursor stationary
        const wx = (mx - v.x) / v.k;
        const wy = (my - v.y) / v.k;
        return { k: nk, x: mx - wx * nk, y: my - wy * nk };
      });
    };
    c.addEventListener('wheel', handler, { passive: false });
    return () => c.removeEventListener('wheel', handler);
  }, []);

  // ── Esc clears selection ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setSel(null); onFocusChangeRef.current?.(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Per-instance prefix for SVG <marker> def ids so multiple graphs on one page
  // don't share global ids — which would make every graph's markerEnd resolve to
  // the first instance's defs and break arrowheads when that instance unmounts.
  // (useId is stable across renders; strip the ':' React decorates it with so the
  // result is a valid id / url(#…) fragment.)
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '') + '-';
  const markers = useMemo(() => buildArrowMarkers(isDark), [isDark]);

  // property-edge muted color — brighter in dark so dotted leaders stay legible on --graph-canvas
  const mutedColor = isDark ? 'var(--color-text-secondary)' : 'var(--color-text-muted)';

  // current LOD: collar only above the threshold and when not a dense graph (unless focused)
  const dense = nodeRecs.length > DENSE_NODE_COUNT;
  const nb = focusId ? neighborsOf[focusId] : null;

  function nodeUseCollar(rec: NodeRec): boolean {
    if (!collarEnabled) return false;
    if (rec.glyphClass !== 'entity') return false;
    if (view.k < COLLAR_LOD_ZOOM) return false;
    if (dense) {
      // on dense graphs only the focused neighborhood gets collars
      if (!focusId) return false;
      if (!(nb && nb[rec.id])) return false;
    }
    return true;
  }

  // ── render math: world→screen via the view transform ──
  const T = (id: string): LayoutPosition | null => positions.get(id) ?? null;

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--graph-canvas)', cursor: 'grab', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* edge layer */}
      <svg width={size.w} height={size.h} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
        <defs>
          {markers.map((m) => (
            <marker key={m.id} id={uid + m.id} markerWidth={m.markerWidth} markerHeight={m.markerHeight} refX={m.refX} refY={m.refY} orient="auto">
              <path d={m.path} fill={m.color} />
            </marker>
          ))}
        </defs>
        <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
          {edgeRecs.map((e) => {
            const pa = T(e.source);
            const pb = T(e.target);
            if (!pa || !pb) return null;
            const { d, control } = bowPath(pa, pb);
            // A v2-styled edge (explicit category colour) is always a real edge:
            // never collapse it to annotation styling even if edgeKind on the v1
            // types would classify it as one. Keeps v2 judgment/capability edges
            // in their category colour + dash.
            const isAnno = (e.kind === 'annotation' || e.isProperty) && !e.v2Color;
            const active = !!focusId && !!nb && !!nb[e.source] && !!nb[e.target] && (e.source === focusId || e.target === focusId);
            const dimmed = !!focusId && !active;
            const srcCat = nodeById.get(e.source)?.category ?? 'network';
            const col = isAnno
              ? mutedColor
              : (e.v2Color
                ?? resolveEdgeColor({ kind: e.kind, critical: e.critical, sourceType: nodeById.get(e.source)?.type, dark: isDark }));
            // v2 judgment-category edges dash; v1 path keeps annotation-only dashing.
            const dashArray = isAnno ? ANNOTATION_DASH : (e.v2Dashed ? V2_JUDGMENT_DASH : undefined);
            const w = edgeWidth({ kind: isAnno ? 'annotation' : e.kind, critical: e.critical, active });
            const op = dimmed ? 0.07 : edgeOpacity({ kind: isAnno ? 'annotation' : e.kind, critical: e.critical, active });
            const showLabel = (active || showEdgeLabels) && !!e.label;
            const labelW = e.label.length * 6.2 + 8;
            return (
              <g key={e.id} style={{ transition: 'opacity 200ms', opacity: dimmed ? 0.07 : 1 }}>
                <path
                  d={d}
                  fill="none"
                  stroke={col}
                  strokeWidth={w}
                  opacity={op}
                  strokeDasharray={dashArray}
                  markerEnd={isAnno ? undefined : `url(#${uid}${arrowMarkerId({ critical: e.critical, sourceCategory: srcCat })})`}
                />
                {active && !isAnno && (
                  <path
                    d={d}
                    fill="none"
                    stroke={col}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeDasharray={FLOW_DASH}
                    style={{ animation: 'obg-flow 0.9s linear infinite', filter: `drop-shadow(0 0 3px ${col}aa)` }}
                  />
                )}
                {showLabel && (
                  <g>
                    <rect x={control.x - labelW / 2} y={control.y - 8} width={labelW} height={15} rx={2} fill="var(--color-surface)" stroke="var(--color-border)" strokeWidth={0.75} />
                    <text x={control.x} y={control.y + 2.5} textAnchor="middle" style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', fill: 'var(--color-text-primary)', fontWeight: 600 }}>{e.label}</text>
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* node layer (absolutely positioned glyphs) */}
      {nodeRecs.map((rec) => {
        const p = T(rec.id);
        if (!p) return null;
        const sx = view.x + p.x * view.k;
        const sy = view.y + p.y * view.k;
        const on = focusId === rec.id;
        const dimmed = !!focusId && !(nb && nb[rec.id]);
        const pathDimmed = hasCriticalPath && !cpResult.nodeIds.has(rec.id);
        const opacity = dimmed
          ? (rec.isProperty ? 0.16 : 0.22)
          : pathDimmed ? 0.3 : (rec.isProperty && !on ? 0.78 : 1);
        const zIndex = sel === rec.id ? 10 : on ? 6 : rec.isProperty ? 1 : 3;
        const glow = sel === rec.id || on;
        // Ontology v2 read-flip: when the pipeline supplies an explicit v2-layer
        // colour, it overrides the v1 type-based palette; otherwise unchanged.
        const cc = rec.layerColor
          ? { fill: rec.layerColor, stroke: rec.layerColor, edge: rec.layerColor }
          : categoryColors(rec.type, isDark);
        // Risk ring: severity wins (real findings always loudest), then
        // validation_state for DNS `domain` nodes, then the category stroke.
        const vsColor = rec.type === 'domain' && rec.validationState
          ? VALIDATION_STATE_COLORS[rec.validationState]
          : undefined;
        const ring = rec.severity
          ? (SEVERITY_COLORS[rec.severity] ?? cc.stroke)
          : (vsColor ?? cc.stroke);
        const sw = rec.severity || vsColor ? 1.75 : 1.25;
        const useCollar = nodeUseCollar(rec);

        let glyph: React.ReactNode;
        if (rec.glyphClass === 'entity') {
          glyph = useCollar
            ? <EntityCollarGlyph id={rec.id} collarType={rec.collarType} icon={rec.icon} fill={cc.fill} stroke={cc.stroke} ring={ring} sw={sw} dark={isDark} hub={rec.hub} glow={glow} />
            : <EntitySimpleGlyph icon={rec.icon} fill={cc.fill} stroke={cc.stroke} ring={ring} sw={sw} dark={isDark} hub={rec.hub} scale={scale} glow={glow} />;
        } else if (rec.glyphClass === 'activity') {
          glyph = <ActivityOctagonGlyph icon={rec.icon} fill={cc.fill} stroke={cc.stroke} ring={ring} sw={sw} dark={isDark} scale={scale} glow={glow} />;
        } else if (rec.glyphClass === 'boundary') {
          glyph = <BoundaryGlobeGlyph scale={scale} glow={glow} />;
        } else if (rec.glyphClass === 'geo') {
          glyph = <GeoPinGlyph color={categoryColors('geolocation', isDark).edge} glow={glow} />;
        } else {
          glyph = <MitreHexGlyph glow={glow} />;
        }

        return (
          <div
            key={rec.id}
            className="obg-node"
            style={{
              position: 'absolute',
              left: sx,
              top: sy,
              transform: `translate(-50%,-50%) scale(${view.k})`,
              transformOrigin: 'center',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 5 * scale,
              width: 140 * scale,
              opacity,
              zIndex,
              transition: 'opacity 200ms',
            }}
            onPointerEnter={() => { setHover(rec.id); onNodeHoverRef.current?.(rec.label, rec.data.aliases as string[] | undefined); }}
            onPointerLeave={() => { setHover(null); onNodeHoverRef.current?.(null); }}
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation();
              const next = sel === rec.id ? null : rec.id;
              setSel(next);
              onNodeClickRef.current?.(rec.id, rec.data);
            }}
          >
            {glyph}
            <NodeLabel klass={rec.glyphClass} label={rec.label} sub={rec.sub} ring={ring} hub={rec.hub} scale={scale} showSub={on} />
          </div>
        );
      })}
    </div>
  );
});
